<template>
  <b-table
    v-if="hasPermission('order.list')"
    :data="data"
    :loading="loading"
    paginated
    backend-pagination
    :total="total"
    :per-page="perPage"
    :current-page="page"
    @page-change="onPageChange"
    aria-next-label="Nächste Seite ->"
    aria-previous-label="<- Vorherige Seite"
    aria-page-label="Seite"
    aria-current-label="Aktuelle Seite"
    backend-sorting
    :default-sort-direction="defaultSortOrder"
    :default-sort="[sortField, sortOrder]"
    @sort="onSort"
  >
    <b-table-column
      label="Abrechnung"
      field="order_number"
      sortable
      v-slot="props"
    >
      {{ props.row.order_number }}
    </b-table-column>
    <b-table-column label="Betrag" field="total_price" sortable v-slot="props">
      {{
        props.row.total_price.toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR',
        })
      }}
    </b-table-column>
    <b-table-column
      label="Status"
      field="order_status_id"
      sortable
      v-slot="props"
    >
      {{ props.row.status }}
    </b-table-column>
    <b-table-column
      label="Kundennr."
      field="customer.customer_number"
      v-slot="props"
    >
      <template v-if="hasPermission('customer.get')">
        <router-link
          :to="{ name: 'client.edit', params: { id: props.row.customer_id } }"
        >
          {{ props.row.customer_number }}
        </router-link>
      </template>
      <template v-else>
        {{ props.row.customer_number }}
      </template>
    </b-table-column>
    <b-table-column label="Kunde" field="last_name" sortable v-slot="props">
      <template v-if="hasPermission('customer.get')">
        <router-link
          :to="{ name: 'client.edit', params: { id: props.row.customer_id } }"
        >
          {{ props.row.address.first_name }} {{ props.row.address.last_name }}
        </router-link>
      </template>
      <template v-else>
        {{ props.row.address.first_name }} {{ props.row.address.last_name }}
      </template>
    </b-table-column>
    <b-table-column label="Datum" field="created_at" sortable v-slot="props">
      {{ props.row.created_at }}
    </b-table-column>
    <b-table-column
      v-if="hasPermission('order.invoice')"
      custom-key="actions"
      cell-class="is-actions-cell"
      v-slot="props"
    >
      <div class="buttons is-right">
        <button
          v-if="props.row.status !== 'Offen'"
          class="button"
          :class="{
            'is-loading':
              downloading.includes(props.row.id) && downloadingInvoice,
          }"
          type="button"
          @click.prevent="downloadInvoice(props.row)"
        >
          <b-icon icon="file-pdf-box" /><span>Rechnung abrufen</span>
        </button>
        <button
          v-if="props.row.status !== 'Offen' && props.row.is_saving_plan_fee"
          class="button"
          :class="{
            'is-loading':
              downloading.includes(props.row.id) && downloadingCredit,
          }"
          type="button"
          @click.prevent="downloadCredit(props.row)"
        >
          <b-icon icon="file-pdf-box" /><span>Gutschrift abrufen</span>
        </button>
        <button
          class="button"
          :class="{
            'is-loading':
              downloading.includes(props.row.id) && downloadingContract,
          }"
          type="button"
          @click.prevent="downloadContract(props.row)"
        >
          <b-icon icon="file-pdf-box" /><span>Kundenvereinbarung abrufen</span>
        </button>
      </div>
    </b-table-column>

    <section slot="empty" class="section">
      <div class="content has-text-grey has-text-centered">
        <template v-if="loading">
          <p>
            <b-icon icon="dots-horizontal" size="is-large" />
          </p>
          <p>Lade Daten...</p>
        </template>
        <template v-else>
          <p>
            <b-icon icon="emoticon-sad" size="is-large" />
          </p>
          <p>Keine Daten vorhanden&hellip;</p>
        </template>
      </div>
    </section>
  </b-table>
</template>
<script>
import { oaxios } from '@/_helpers'

import fileDownload from 'js-file-download'
import { mapGetters } from 'vuex'

export default {
  name: 'order-list',
  props: {
    customerId: {
      type: Number,
    },
    search: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      data: [],
      total: 0,
      loading: false,
      downloading: [],
      downloadingInvoice: false,
      downloadingCredit: false,
      downloadingContract: false,
      sortField: 'order_number',
      sortOrder: 'desc',
      defaultSortOrder: 'desc',
      page: 1,
      perPage: 15,
    }
  },
  computed: {
    ...mapGetters('permission', ['hasPermission']),
  },
  watch: {
    search() {
      this.page = 1
      this.loadAsyncData()
    },
  },
  methods: {
    loadAsyncData() {
      if (this.hasPermission('order.list')) {
        this.loading = true
        const data = {
          customer_id: this.customerId,
          search: this.search,
          sort_field: this.sortField,
          sort_order: this.sortOrder,
        }

        oaxios(`/order/list?page=${this.page}`, data)
          .then((r) => {
            this.data = r.data.data
            this.total = r.data.meta.total
            this.perPage = r.data.meta.per_page
          })
          .catch((e) => {
            this.data = []
            this.total = 0
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    onPageChange(page) {
      this.page = page
      this.loadAsyncData()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadAsyncData()
    },
    downloadInvoice(order) {
      if (this.hasPermission('order.invoice')) {
        this.downloading.push(order.id)
        this.downloadingInvoice = true
        const data = {
          order_id: order.id,
        }
        let url = '/order/invoice'
        console.log(order)
        if (order.total_price<0) {
          url = '/order/invoicepurchase'
        }
        oaxios(url, data, 'post', 'blob')
          .then((r) => {
            fileDownload(r.data, `Rechnung_${order.order_number}.pdf`)
          })
          .catch(() => {})
          .finally(() => {
            this.downloading.remove(order.id)
            this.downloadingInvoice = false
          })
      }
    },
    downloadCredit(order) {
      if (this.hasPermission('order.invoice') && order.is_saving_plan_fee) {
        this.downloading.push(order.id)
        this.downloadingCredit = true
        const data = {
          order_id: order.id,
          document_type: 'credit',
        }
        let url = '/order/invoice'
        if (order.total_price<0) {
          url = '/order/invoicepurchase'
        }
        oaxios(url, data, 'post', 'blob')
          .then((r) => {
            fileDownload(r.data, `Gutschrift_${order.order_number}.pdf`)
          })
          .catch(() => {})
          .finally(() => {
            this.downloading.remove(order.id)
            this.downloadingCredit = false
          })
      }
    },
    downloadContract(order) {
      if (this.hasPermission('order.invoice')) {
        this.downloading.push(order.id)
        this.downloadingContract = true
        const data = {
          order_id: order.id,
        }

        oaxios('/order/contract', data, 'post', 'blob')
          .then((r) => {
            fileDownload(r.data, `Kundenvereinbarung_${order.order_number}.pdf`)
          })
          .catch(() => {})
          .finally(() => {
            this.downloading.remove(order.id)
            this.downloadingContract = false
          })
      }
    },
  },
  filters: {
    /**
     * Filter to truncate string, accepts a length parameter
     */
    truncate(value, length) {
      return value.length > length ? value.substr(0, length) + '...' : value
    },
  },
  mounted() {
    this.loadAsyncData()
  },
}
</script>
